import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../services/api';
import {
  QrCode, Calendar, Bus, AlertCircle, CheckCircle, Clock, ChevronDown, ChevronUp, Mail, Phone, AlertTriangle
} from 'lucide-react';

const Abbonamenti = () => {
  const [abbonamenti, setAbbonamenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { caricaAbbonamenti(); }, []);

  const caricaAbbonamenti = async () => {
    try {
      setLoading(true);
      const response = await api.get('/abbonamenti/abbonamenti/miei_abbonamenti/');
      const abbs = response.data.results || response.data || [];
      setAbbonamenti(abbs);
      const primoAttivo = abbs.find(a => a.stato === 'ATTIVO');
      if (primoAttivo) setExpandedId(primoAttivo.id);
    } catch (error) {
      console.error('Errore:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatData = (dataStr) => {
    return new Date(dataStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const calcolaGiorniRimanenti = (dataFine) => {
    const oggi = new Date();
    const fine = new Date(dataFine);
    return Math.ceil((fine - oggi) / (1000 * 60 * 60 * 24));
  };

  const handleContattoRinnovo = (tipo) => {
    if (tipo === 'email') {
      window.location.href = 'mailto:info@casellanoleggiobus.it?subject=Richiesta Rinnovo Abbonamento';
    } else if (tipo === 'telefono') {
      window.location.href = 'tel:+393331234567';
    }
  };

  const abbonamentiInScadenza = abbonamenti.filter(abb => {
    const giorni = calcolaGiorniRimanenti(abb.data_fine);
    return abb.stato === 'ATTIVO' && giorni > 0 && giorni <= 7;
  });

  if (loading) {
    return (
      <div className="du-loading">
        <div className="du-spinner"></div>
        <p>Caricamento abbonamenti...</p>
      </div>
    );
  }

  if (abbonamenti.length === 0) {
    return (
      <div className="du-empty">
        <QrCode size={56} strokeWidth={1.5} />
        <h3>Nessun Abbonamento</h3>
        <p>Contatta l'ufficio per attivare un abbonamento</p>
      </div>
    );
  }

  return (
    <div className="du-abb-list">
      {/* Alert scadenza globale */}
      {abbonamentiInScadenza.length > 0 && (
        <div className="du-alert-banner">
          <AlertTriangle size={24} className="icon" />
          <div>
            <h3 className="du-alert-title">
              {abbonamentiInScadenza.length === 1 ? 'Abbonamento' : 'Abbonamenti'} in Scadenza
            </h3>
            {abbonamentiInScadenza.map((abb) => {
              const giorni = calcolaGiorniRimanenti(abb.data_fine);
              return (
                <p key={abb.id} className="du-alert-msg">
                  <strong>{abb.tratta || abb.linea_nome}</strong> scade tra <strong>{giorni} {giorni === 1 ? 'giorno' : 'giorni'}</strong>
                </p>
              );
            })}
            <div className="du-alert-actions">
              <button className="du-alert-btn primary" onClick={() => handleContattoRinnovo('email')}>
                <Mail size={14} /> Email per Rinnovo
              </button>
              <button className="du-alert-btn secondary" onClick={() => handleContattoRinnovo('telefono')}>
                <Phone size={14} /> Chiama
              </button>
            </div>
          </div>
        </div>
      )}

      {abbonamenti.map((abb) => {
        const giorni = calcolaGiorniRimanenti(abb.data_fine);
        const attivo = abb.stato === 'ATTIVO';
        const scadenza = attivo && giorni <= 7 && giorni > 0;
        const scaduto = giorni <= 0;
        const expanded = expandedId === abb.id;

        const cardClass = `du-abb-card ${attivo && !scaduto ? 'active' : ''} ${scadenza ? 'expiring' : ''}`;

        return (
          <div key={abb.id} className={cardClass}>
            {/* Alert in-card */}
            {scadenza && (
              <div className="du-abb-card-alert">
                <AlertTriangle size={16} />
                <div className="texts">
                  <strong>Scade tra {giorni} {giorni === 1 ? 'giorno' : 'giorni'}!</strong>
                  <span>Contattaci per rinnovarlo</span>
                </div>
              </div>
            )}

            {/* Top */}
            <div className="du-abb-top">
              <div className="du-abb-line">
                <div className="du-abb-line-icon"><Bus size={20} /></div>
                <div>
                  <h3>{abb.tratta || abb.linea_nome}</h3>
                  <p className="subtitle">{abb.titolare_nome}</p>
                </div>
              </div>
              <span className={`du-abb-badge ${attivo && !scaduto && !scadenza ? 'active' : scadenza ? 'expiring' : 'inactive'}`}>
                {attivo && !scaduto && !scadenza ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
                {attivo && !scadenza ? 'Attivo' : scadenza ? `${giorni}g` : 'Inattivo'}
              </span>
            </div>

            {/* Info */}
            <div className="du-abb-info">
              <div className="du-abb-row">
                <Calendar size={15} />
                <div className="texts">
                  <span className="lbl">Validità</span>
                  <span className="val">{formatData(abb.data_inizio)} – {formatData(abb.data_fine)}</span>
                </div>
              </div>
              {giorni > 0 && (
                <div className="du-abb-row">
                  <Clock size={15} />
                  <div className="texts">
                    <span className="lbl">Giorni rimanenti</span>
                    <span className={`val ${scadenza ? 'warn' : ''}`}>
                      {giorni} {giorni === 1 ? 'giorno' : 'giorni'}
                    </span>
                  </div>
                </div>
              )}
              <div className="du-abb-row">
                <span className="hash">#</span>
                <div className="texts">
                  <span className="lbl">Codice</span>
                  <span className="val mono">{abb.codice}</span>
                </div>
              </div>
            </div>

            {/* Rinnovo */}
            {scadenza && (
              <div className="du-abb-renew">
                <button className="du-abb-renew-btn" onClick={() => handleContattoRinnovo('email')}>
                  <Mail size={14} /> Contatta per Rinnovo
                </button>
              </div>
            )}

            {/* QR */}
            {attivo && !scaduto ? (
              <>
                <button className="du-qr-toggle" onClick={() => setExpandedId(expanded ? null : abb.id)}>
                  <QrCode size={18} />
                  {expanded ? 'Nascondi' : 'Mostra'} QR Code
                  {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {expanded && (
                  <div className="du-qr-area">
                    <div className="du-qr-box">
                      <QRCodeSVG value={abb.codice} size={220} level="H" includeMargin={true} />
                    </div>
                    <p className="du-qr-code-text">{abb.codice}</p>
                    <p className="du-qr-hint">Mostra questo codice all'autista</p>
                  </div>
                )}
              </>
            ) : (
              <div className="du-qr-disabled">
                <AlertCircle size={16} />
                <span>QR disponibile solo per abbonamenti attivi</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default Abbonamenti;
